'use client';
import { useState, useMemo, type ComponentPropsWithoutRef } from 'react';

export interface DynamicImageProps extends ComponentPropsWithoutRef<'img'> {
  dynamicUrl?: string;
  defaultUrl?: string;
  resizedWidth: number;
}

function buildResizedImageHref(originHref: string, resizedWidth: number) {
  try {
    const url = new URL(originHref);
    const search = new URLSearchParams(url.search);
    search.append('width', String(resizedWidth));
    search.append('format', 'webp');
    url.search = search.toString();
    return url.toString();
  } catch {
    return '';
  }
}

function getDynamicSrc(dynamicUrl: string, resizedWidth: number, x: number) {
  return `${buildResizedImageHref(dynamicUrl, resizedWidth * x)} ${x}x`;
}

export const DynamicImage = ({
  dynamicUrl,
  resizedWidth,
  defaultUrl,
  ...props
}: DynamicImageProps) => {
  const [isError, setIsError] = useState(false);
  const src = useMemo(() => {
    if (defaultUrl) {
      return defaultUrl;
    }
    if (dynamicUrl != null && !isError) {
      return getDynamicSrc(dynamicUrl, resizedWidth, 2);
    }
    return '';
  }, [defaultUrl, dynamicUrl, resizedWidth, isError]);

  const srcSet = useMemo(() => {
    return dynamicUrl && !isError
      ? [
          getDynamicSrc(dynamicUrl, resizedWidth, 1),
          getDynamicSrc(dynamicUrl, resizedWidth, 2),
        ].join(', ')
      : undefined;
  }, [dynamicUrl, isError, resizedWidth]);

  if (!src && !srcSet) {
    return null;
  }

  return (
    <picture>
      {dynamicUrl != null && !isError && <source srcSet={srcSet} />}
      <img
        {...props}
        alt={props.alt ?? 'default'}
        className={props.className}
        onError={() => setIsError(true)}
        src={src}
      />
    </picture>
  );
};

DynamicImage.displayName = 'DynamicImage';
